/** @jsx jsx */
import React from 'react';
import { css, jsx, SerializedStyles } from '@emotion/core';
import ReactDatePicker, { registerLocale, ReactDatePickerProps } from 'react-datepicker';
import ja from 'date-fns/locale/ja';
import 'react-datepicker/dist/react-datepicker.css';

import {
  grayScale, textFontSize, utilityColor, layer,
} from 'components/styles';
import Icon from 'components/atoms/Icon';
import AddOnInput from 'components/atoms/Form/AddOnInput';

// カレンダーの日本語化
const jaLocale = {
  ...ja,
  options: {
    weekStartsOn: 0, /* Monday */

    firstWeekContainsDate: 1,
  },
};
registerLocale('ja', jaLocale);

const styles = {
  wrapper: css({
    '.react-datepicker-popper': {
      zIndex: 1000,
      left: '-29px!important',
    },
  }),
  icon: css({
    padding: '8px 0 8px 8px',
  }),
  input: css({
    width: '90px',
    padding: '0 8px',
    boxSizing: 'border-box',
    border: 'transparent',
    fontSize: textFontSize.re,
    fontFamily: 'inherit',
    outline: 0,
    ':disabled': {
      backgroundColor: grayScale.gray05,
    },
  }),
  inputFullWidth: css({
    width: '100%',
  }),
  calendarContainer: css({
    overflow: 'hidden',
    padding: 0,
    backgroundColor: utilityColor.white,
    boxShadow: layer.layer01,
    zIndex: 99999,
    borderRadius: '4px',
  }),
  disabledAddOnInput: css({
    backgroundColor: grayScale.gray05,
  }),
};


const DatePicker: React.FC<{
  date?: Date;
  changeDate: (arg: any) => void;
  dateFormat?: string;
  isFullWidth?: boolean;
  selectStart?: boolean;
  selectEnd?: boolean;
  showMonthYearPicker?: boolean;
  startDate?: Date;
  endDate?: Date;
  customStyle?: SerializedStyles;
  disabled?: boolean;
  isShowDateOnDisable? : boolean;
  readOnly? : boolean;
  showYearDropdown? : boolean;
  minDate?: Date
  maxDate?: Date
  noDeleteDate?: boolean;
  hideIcon?:boolean;
  positionFixed?:boolean;
  /** 入力欄の id（label の htmlFor から指すとき）。指定しなければ付けない */
  id?: string;
  /** カレンダーの位置の調整（popper.js の modifiers）。指定しなければ react-datepicker の既定 */
  popperModifiers?: ReactDatePickerProps['popperModifiers'];
}> = ({
  id, popperModifiers,
  date, changeDate,
  dateFormat = 'yyyy/MM/dd',
  isFullWidth = false,
  selectStart = false,
  selectEnd = false,
  showMonthYearPicker = false,
  startDate,
  endDate,
  customStyle,
  disabled = false,
  isShowDateOnDisable = false,
  readOnly = false,
  showYearDropdown = true,
  minDate, maxDate,
  noDeleteDate = true,
  hideIcon,
  positionFixed = false,
}) => (
  <div css={styles.wrapper}>

    <AddOnInput
      input={(
        <ReactDatePicker
        // settings
          id={id}
          locale="ja"
          dateFormat={dateFormat}
          selected={disabled && !isShowDateOnDisable ? undefined : date}
          onChange={(inputDate: Date) => changeDate(inputDate)}
          selectsStart={selectStart}
          selectsEnd={selectEnd}
          startDate={startDate}
          endDate={endDate}
          disabled={disabled}
          readOnly={readOnly}
          minDate={minDate}
          maxDate={maxDate}
        // styling
          css={css(styles.input, isFullWidth && styles.inputFullWidth)}
          showPopperArrow={false}
          showMonthYearPicker={showMonthYearPicker}
          showYearDropdown={showYearDropdown}
          dropdownMode="select"
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
            if (noDeleteDate === true) {
              e.preventDefault();
            }
          }}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            if (noDeleteDate === true) {
              e.target.style.caretColor = 'transparent';
            }
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            if (noDeleteDate === true) {
              e.target.style.caretColor = 'auto';
            }
            if (isNaN(Date.parse(e.target.value)) || (e.target.value.match(/\//g) || []).length > 2) {
              changeDate(null);
            }
          }}
          popperProps={{ positionFixed }}
          popperModifiers={popperModifiers}
        />

    )}
      addOn={(
        <div style={{display:hideIcon ? 'none' : ''}} css={styles.icon}>
          <Icon type="calendar" color={grayScale.gray100} />
        </div>
      )}
      appended={false}
      customStyle={css(disabled ? styles.disabledAddOnInput : undefined, customStyle)}
    />
  </div>
);

export default DatePicker;
